import * as m from '@staiter/i18n';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { type ToastEmit, ToastProvider, emitToast } from '~/components/feedback';
import { isAccessDecision } from '~/lib/api/access-decision';
import { findStaleEntityPath, installActiveEntitySync } from '~/lib/api/active-entity-sync';
import { fetchMe } from '~/lib/api/auth';
import { onEntityForbidden } from '~/lib/api/client';
import { ApiRequestError } from '~/lib/api/client';
import { isEntityForbidden } from '~/lib/api/entity-forbidden';
import { noticeEntityRevoked } from '~/lib/api/entity-revoked-notice';
import { renderApiError } from '~/lib/api/error-renderer';
import { installOnlineTruth } from '~/lib/api/online-truth';
import { installPausedMutationWatcher } from '~/lib/api/paused-mutation-watch';
import { createMeRefresher } from '~/lib/auth/refreshMeOnScopeChange';
import { useTaskFilterStore } from '~/lib/tasks/stores/taskFilterStore';
import { readEntityName, useActiveEntityStore } from '~/store/active-entity';
import { useAuthStore } from '~/store/auth';
import { RuntimeLocaleProvider } from './RuntimeLocaleProvider';
import { router } from './router';

/**
 * E-FE.1 — Unified toast-error flow. Вся локализация — в `renderApiError`.
 *
 * Никогда не вставляем raw `err.message` в toast.description: backend может прислать
 * английскую фразу из Hono/Zod, FE нарушит I1b (error-toast без machine-текста).
 */
function describeError(err: Error): ToastEmit {
  const rendered = renderApiError(err);
  // S-FIX-5-FE.9.1: trace_id отдельным полем (не в description), всегда виден.
  const traceId =
    err instanceof ApiRequestError && err.traceId
      ? err.traceId.slice(0, 8)
      : rendered.trace_id
        ? rendered.trace_id.slice(0, 8)
        : undefined;

  // Variant: 429/503 — warning; остальное — error. Смотрим на HTTP status если он доступен.
  let variant: ToastEmit['variant'] = 'error';
  if (err instanceof ApiRequestError) {
    if (err.status === 429 || err.status === 503) variant = 'warning';
  }

  return {
    variant,
    title: rendered.title,
    description: rendered.body,
    ...(traceId ? { traceId } : {}),
  };
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: 1,
      },
      /**
       * F1 — мутации не встают в очередь ожидания сети.
       *
       * Дефолт TanStack `networkMode: 'online'` — поведение PWA с офлайн-очередью:
       * при офлайне мутация ждёт возврата сети сколько угодно, не вызывая `mutationFn`
       * и не сообщая об этом. У нас офлайн-очередь — не-цель (SOLUTION §6.1 п.8):
       * изменение либо уходит на сервер, либо откатывается на глазах с тостом (I1).
       * `always` даёт именно это: без сети запрос падает сразу, `onError` возвращает
       * оптимистик и называет задачу.
       */
      mutations: {
        networkMode: 'always',
      },
    },
    queryCache: new QueryCache({
      onError: (err, query) => {
        // U0.3 — query может пометить себя через meta.suppressErrorToast,
        // чтобы error state рендерился inline (per-tab ErrorState) без глобального toast.
        if ((query.meta as { suppressErrorToast?: boolean } | undefined)?.suppressErrorToast) {
          return;
        }
        // Отказ модульного гейта показывает `ModuleGuard`, а не тост (гейт захода 6).
        if (isAccessDecision(err)) return;
        emitToast(describeError(err));
      },
    }),
    mutationCache: new MutationCache({
      onError: (err, _variables, _context, mutation) => {
        // U0.3-паритет с QueryCache — mutation.meta.suppressErrorToast → компонент
        // обрабатывает ошибку сам (inline/dialog), без глобального toast.
        if ((mutation.meta as { suppressErrorToast?: boolean } | undefined)?.suppressErrorToast) {
          return;
        }
        // С-I3 Р.1, круг 2 гейта (UX, MINOR): запись сорвалась из-за отключённого доступа к компании —
        // не справка, а предупреждение: человек нажал «Провести» и должен знать, что не сохранилось.
        if (isEntityForbidden(err)) {
          emitToast({
            variant: 'warning',
            title: m.access_write_not_saved_title(),
            description: m.access_write_not_saved_body(),
          });
          return;
        }
        if (isAccessDecision(err)) return;
        emitToast(describeError(err));
      },
    }),
  });
}

export function AppProviders({ children }: { children: ReactNode }): JSX.Element {
  const [queryClient, setQueryClient] = useState(createQueryClient);
  // hotfix 2026-07-09 — эпоха охвата: key-bump на QueryClientProvider ремаунтит
  // поддерево, гарантируя пересоздание ВСЕХ query-обзерверов против пустого кэша
  // (swap клиента сам по себе на проде не давал ремаунт-рефетча).
  const [scopeEpoch, setScopeEpoch] = useState(0);
  const clientRef = useRef(queryClient);

  // F1 — детектор онлайна читает `navigator.onLine`, а не верит одиночному событию.
  // Ставится один раз на приложение: onlineManager глобален и клиента не знает.
  useEffect(() => {
    installOnlineTruth();
  }, []);

  // F2 — сторож I1 переустанавливается на КАЖДЫЙ клиент: при смене компании клиент
  // пересоздаётся, и подписка на прежний mutationCache перестала бы что-либо видеть.
  useEffect(() => {
    return installPausedMutationWatcher(queryClient);
  }, [queryClient]);

  // Дев-аффорданс живых проверок: без ссылки на клиент искусственную paused-мутацию
  // из консоли не построить, и AC сторожа проверялся бы только юнитом.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __staiterQueryClient?: QueryClient }).__staiterQueryClient =
      queryClient;
  }, [queryClient]);

  // FM-S-multi-4-3 (rev. access-go-live) — смена активной компании/охвата:
  // кэш прежней компании целиком мёртв, поэтому НЕ invalidate (он рефетчит mounted-запросы
  // со старыми ID под новым X-Active-Entity → 404-шторм), а:
  // 1) если URL держит UUID объекта прежней компании — уводим на списочный маршрут;
  // 2) cancel гасит in-flight запросы (их ошибки не долетают до тостов);
  // 3) свежий QueryClient перезапускает загрузку с чистого листа — производные запросы
  //    стреляют только по ID из свежих родительских списков.
  // С-I3 Р.1 (ACC-B3) — запрос получил 403 ENTITY_FORBIDDEN (членство в компании отключили
  // посреди сессии): профиль перечитывается — `fetchMe` обновит токен на активную компанию и
  // снимет выбор отключённой (сброс кэша — через смену компании), без активных — выход. Один
  // разбор на всплеск: параллельные запросы страницы падают разом.
  useEffect(() => {
    let inFlight: Promise<void> | null = null;
    onEntityForbidden(() => {
      if (inFlight) return;
      const before = useAuthStore.getState().user;
      inFlight = fetchMe()
        .then((me) => {
          useAuthStore.getState().setMe(me);
          // Круг 1 гейта Р.1 (UI и UX, MAJOR): компания сменилась молча — один спокойный тост
          // называет, к чему отключён доступ и где человек теперь.
          const was = before?.active_entity_id ?? null;
          const now = me.active_entity_id ?? null;
          if (was && now && was !== now) {
            noticeEntityRevoked(
              was,
              before?.active_entity_name ?? null,
              me.active_entity_name ?? null,
            );
          }
        })
        .catch(() => undefined)
        .finally(() => {
          setTimeout(() => {
            inFlight = null;
          }, 3000);
        });
    });
    return () => onEntityForbidden(null);
  }, []);

  // Круг 1 гейта Р.1 (UX, MAJOR): сохранённая компания недоступна при загрузке страницы (доступ
  // отозвали, пока вкладки не было) — тот же тост, имя — из справочника имён этого браузера.
  useEffect(
    () =>
      useActiveEntityStore.subscribe((s, prev) => {
        if (!s.staleStoredId || s.staleStoredId === prev.staleStoredId) return;
        const to = s.memberships.find((x) => x.entity_id === s.activeEntityId)?.entity_name ?? null;
        noticeEntityRevoked(s.staleStoredId, readEntityName(s.staleStoredId), to);
        // Плашка «Сохранённая компания недоступна… 00000000» говорила то же сырым id — тост с именами
        // её заменяет, двух сообщений об одном событии не нужно.
        useActiveEntityStore.getState().clearStaleWarning();
      }),
    [],
  );

  useEffect(() => {
    // ACC-F1 (пак `tails-closeout`) — модули новой компании приходят с профилем.
    const refreshMe = createMeRefresher({
      fetchMe,
      setMe: (me) => useAuthStore.getState().setMe(me),
    });
    return installActiveEntitySync(() => {
      void refreshMe();
      const loc = router.state.location;
      const safePath = findStaleEntityPath(loc.pathname, loc.searchStr);
      if (safePath !== null) router.history.replace(safePath);
      // chat-interactive-actions F1 [П3] — фильтры задач живут вне QueryClient и
      // пересозданием кэша не чистятся. Фильтр по проектам ПРЕЖНЕЙ компании,
      // переживший переключение, показал бы пустой список там, где задачи есть, —
      // и это читалось бы как «данные пропали», а не как «фильтр не тот».
      useTaskFilterStore.getState().resetAll();
      void clientRef.current.cancelQueries();
      const fresh = createQueryClient();
      clientRef.current = fresh;
      setQueryClient(fresh);
      setScopeEpoch((e) => e + 1);
    });
  }, []);

  return (
    <QueryClientProvider key={scopeEpoch} client={queryClient}>
      <RuntimeLocaleProvider>
        <ToastProvider>{children}</ToastProvider>
      </RuntimeLocaleProvider>
    </QueryClientProvider>
  );
}
